import { type FC, type KeyboardEvent, type PropsWithChildren, type ReactNode, useEffect, useId, useRef } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import TimesIcon from '../../assets/TimesIcon.svg';
import ReactDOM from 'react-dom';
import { useEscapeKeyHandler } from '../../lib/hooks/useEscapeKeyHandler';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export const enum ModalSize {
  MEDIUM = 'medium',
  LARGE = 'large',
}

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  title?: ReactNode;
  hideCloseButton?: boolean;
  zIndex?: number;
  clickOutsideClose?: boolean;
  escapeKeyClose?: boolean;
  disableBodyScroll?: boolean;
  size?: ModalSize;
  /** id of the element that names the dialog, for modals that render their own header instead of `title` */
  labelledBy?: string;
} & PropsWithChildren;

export const Modal: FC<ModalProps> = ({
  title,
  isOpen,
  onClose,
  hideCloseButton,
  zIndex,
  children,
  clickOutsideClose,
  escapeKeyClose,
  disableBodyScroll,
  size,
  labelledBy,
}: ModalProps) => {
  const showDefaultModalHeader = title || !hideCloseButton;
  const titleId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  useEscapeKeyHandler({ active: isOpen && !!escapeKeyClose, onEscapePressed: onClose });
  useDialogFocus(containerRef, isOpen);

  const modalRoot = document.getElementById('modal');
  if (!isOpen || !modalRoot) {
    return null;
  }

  const ModalTemplate = (
    <>
      {disableBodyScroll && <PreventBodyScrollStyle />}
      <ModalOverlay zIndex={zIndex} onClick={() => clickOutsideClose && onClose()}>
        <ModalContainer
          ref={containerRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={labelledBy ?? (title ? titleId : undefined)}
          tabIndex={-1}
          onKeyDown={keepFocusInside}
          onClick={(e) => e.stopPropagation()}
          size={size}
        >
          {showDefaultModalHeader && (
            <ModalHeader>
              {title && <Title id={titleId}>{title}</Title>}
              {!hideCloseButton && (
                <CloseButton type="button" aria-label="Close" onClick={onClose}>
                  <TimesIcon />
                </CloseButton>
              )}
            </ModalHeader>
          )}
          {children}
        </ModalContainer>
      </ModalOverlay>
    </>
  );

  return ReactDOM.createPortal(ModalTemplate, modalRoot);
};

/** Moves focus into the dialog while it is open and hands it back to whatever had it before. */
const useDialogFocus = (containerRef: React.RefObject<HTMLDivElement | null>, isOpen: boolean) => {
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    containerRef.current?.focus();
    return () => previouslyFocused?.focus();
  }, [containerRef, isOpen]);
};

/** Wraps Tab and Shift+Tab around the dialog's focusable elements so focus cannot leave it. */
const keepFocusInside = (e: KeyboardEvent<HTMLDivElement>) => {
  if (e.key !== 'Tab') {
    return;
  }
  const focusable = Array.from(e.currentTarget.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (!first || !last) {
    e.preventDefault();
    return;
  }
  const active = document.activeElement;
  if (e.shiftKey && (active === first || active === e.currentTarget)) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && active === last) {
    e.preventDefault();
    first.focus();
  }
};

const ModalOverlay = styled.div<{ zIndex?: number }>`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.25);
  z-index: ${({ zIndex }) => (zIndex ? zIndex : 999)};
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding: 16px 24px;
`;

const Title = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.medium};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.prototype.foreground};
  text-transform: uppercase;
`;

const ModalContainer = styled.div<{ size?: ModalSize }>`
  position: relative;
  overflow-x: hidden;
  min-width: 388px;
  min-height: 185px;
  max-height: 95vh;
  max-width: 90vw;
  display: flex;
  border-radius: 16px;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.prototype.card};
  box-shadow: ${({ theme }) => theme.shadow.light};
  outline: none;
  ${({ size }) =>
    size &&
    `
    width: ${size === ModalSize.MEDIUM ? '60%' : '90%'};
    height: ${size === ModalSize.MEDIUM ? 'auto' : '80%'};
  `}
`;

const CloseButton = styled.button`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  background: none;
  border: none;
  font-size: ${({ theme }) => theme.fontSizes.medium};
  cursor: pointer;
`;

const PreventBodyScrollStyle = createGlobalStyle`
    body {
        overflow: hidden;
    }
`;
